import styled from 'styled-components';

import { IconButton } from '@/ui/Button';
import { useCourseDrawerItemEdit } from '@/features/course/admin/hooks/useCourseDrawerItemEdit';

type FileCardProps = {
  fileExtension: string;
  fileTitle: string;
  downloadUrl: string;
  onFileTitleChange: (newDocumentName: string) => void;
};

const FileCardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: 88px;
  padding: 0 16px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  gap: 16px;
  border-radius: 12px;
  width: 100%;
`;

const Informer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  width: 56px;
  background: ${({ theme }) => theme.colors.secondary.container};
  border-radius: 8px;
  flex-shrink: 0;
`;

const StyledInformerSpan = styled.div`
  color: ${({ theme }) => theme.colors.tertiary.onContainer};
  ${(p) => p.theme.typography.overline};
  white-space: nowrap;
  overflow: hidden;
  max-width: 90%;
  text-overflow: ellipsis;
`;

const StyledTitleContainer = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 2px;
`;

const StyledTitle = styled.div`
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  ${(p) => p.theme.typography.title4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const FileCardAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  size: 16px;
  Button {
    font-size: 16px;
  }
`;

const FileTitleInput = styled.input`
  ${({ theme }) => theme.typography.title4};
  color: ${({ theme }) => theme.colors.neutral.onBackground};

  background: transparent;
  width: 100%;
  border-radius: 4px;
  outline: none;
  border: none;
  padding: 0;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary.outline};
  }
`;

export const FileCard = ({ fileExtension, fileTitle, downloadUrl, onFileTitleChange }: FileCardProps): JSX.Element => {
  const {
    titleInputRef,
    containerRef,
    updatedTitle,
    titleEditingEnabled,
    onUpdatedTitleChange,
    onEnableTitleEditing,
    onEditorKeyUp,
  } = useCourseDrawerItemEdit({ onTitleChangeSubmit: (value) => onFileTitleChange(value), title: fileTitle });

  return (
    <FileCardContainer contentEditable={false}>
      <Informer>
        <StyledInformerSpan>{fileExtension}</StyledInformerSpan>
      </Informer>
      <StyledTitleContainer ref={containerRef}>
        {titleEditingEnabled ? (
          <FileTitleInput
            ref={titleInputRef}
            value={updatedTitle}
            onKeyUp={onEditorKeyUp}
            onChange={(event) => onUpdatedTitleChange(event.target.value)}
            placeholder="Untitled"
          />
        ) : (
          <StyledTitle onDoubleClick={onEnableTitleEditing}>{fileTitle}</StyledTitle>
        )}
      </StyledTitleContainer>
      <FileCardAction>
        <IconButton href={downloadUrl} target="_blank" icon={['far', 'arrow-down-to-line']} />
      </FileCardAction>
    </FileCardContainer>
  );
};
